"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DerToHexSignature = exports.getSignature = exports.getPublicKey = exports.getDevicePaths = exports.getAppVersion = exports.getAppName = void 0;
const neon_core_1 = require("@cityofzion/neon-core");
const ErrorCode_1 = require("./ErrorCode");
/**
 * Duplicated from @cityofzion/neon-ledger due to package incompatibility:
 * transpiled JS version throws "Cannot find module './main'"
 */
const DEFAULT_STATUSLIST = [ErrorCode_1.StatusWord.OK];
var Command;
(function (Command) {
    Command[Command["GET_APP_NAME"] = 0] = "GET_APP_NAME";
    Command[Command["GET_VERSION"] = 1] = "GET_VERSION";
    Command[Command["SIGN_TX"] = 2] = "SIGN_TX";
    Command[Command["GET_PUBLIC_KEY"] = 4] = "GET_PUBLIC_KEY";
})(Command || (Command = {}));
/**
 * Helper to send data chunks to sign.
 * @param ledger - Ledger instance
 * @param msg - data up to 510 character
 * @param chunk - data sequence number. Start at 0, increase by 1
 * @param finalChunk - set to true if this is the last chunk for the command
 */
function sendDataToSign(ledger, msg, chunk, finalChunk = false) {
    return __awaiter(this, void 0, void 0, function* () {
        return yield ledger.send(0x80, Command.SIGN_TX, chunk, finalChunk ? 0x00 : 0x80, Buffer.from(msg, "hex"), DEFAULT_STATUSLIST);
    });
}
/**
 * Requests the open application name from the Ledger.
 * @param ledger - Ledger instance
 * @returns the ledger application name. Expected "NEO3"
 */
function getAppName(ledger) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield ledger.send(0x80, Command.GET_APP_NAME, 0x00, 0x00, undefined, DEFAULT_STATUSLIST);
            const version = response.toString("ascii");
            return version.substring(0, version.length - 2); // take of status word
        }
        catch (e) {
            if ((0, ErrorCode_1.looksLikeTransportStatusError)(e)) {
                throw (0, ErrorCode_1.evalTransportError)(e);
            }
            throw e;
        }
    });
}
exports.getAppName = getAppName;
/**
 * Requests the application version from the Ledger.
 * @param ledger - Ledger instance
 * @returns the application version in Major.Minor.Patch format i.e. "0.1.0"
 */
function getAppVersion(ledger) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield ledger.send(0x80, Command.GET_VERSION, 0x00, 0x00, undefined, DEFAULT_STATUSLIST);
            const major = response.readUInt8(0);
            const minor = response.readUInt8(1);
            const patch = response.readUInt8(2);
            return major.toString() + "." + minor.toString() + "." + patch.toString();
        }
        catch (e) {
            if ((0, ErrorCode_1.looksLikeTransportStatusError)(e)) {
                throw (0, ErrorCode_1.evalTransportError)(e);
            }
            throw e;
        }
    });
}
exports.getAppVersion = getAppVersion;
/**
 * Returns the list of connected Ledger devices. Throw if Ledger is not supported by the computer.
 * @param ledgerLibrary - Ledger library
 */
function getDevicePaths(ledgerLibrary) {
    return __awaiter(this, void 0, void 0, function* () {
        const supported = yield ledgerLibrary.isSupported();
        if (!supported) {
            throw new Error(`Your computer does not support the ledger!`);
        }
        return yield ledgerLibrary.list();
    });
}
exports.getDevicePaths = getDevicePaths;
/**
 * Requests the public key of a requested address from the Ledger.
 * @param ledger - Ledger instance
 * @param bip44String - BIP44 string (40 bytes)
 * @param showAddressOnDevice - whether to show the public key as NEO3 address
 * on the Ledger
 * @returns An unencoded public key (65 bytes)
 */
function getPublicKey(ledger, bip44String, showAddressOnDevice = false) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield ledger.send(0x80, Command.GET_PUBLIC_KEY, 0x00, showAddressOnDevice ? 0x01 : 0x0, Buffer.from(bip44String, "hex"), DEFAULT_STATUSLIST);
            return response.toString("hex").substring(0, 130);
        }
        catch (e) {
            if ((0, ErrorCode_1.looksLikeTransportStatusError)(e)) {
                throw (0, ErrorCode_1.evalTransportError)(e);
            }
            throw e;
        }
    });
}
exports.getPublicKey = getPublicKey;
/**
 * Requests the device to sign a message using the NEO application.
 * @param ledger - Ledger instance
 * @param payload - message to sign as a hexstring.
 * @param bip44String - BIP44 string (40 bytes)
 * @param network - MainNet, TestNet or custom network number
 * @returns Signature as a hexstring (64 bytes)
 */
function getSignature(ledger, payload, bip44String, network) {
    return __awaiter(this, void 0, void 0, function* () {
        yield sendDataToSign(ledger, bip44String, 0);
        yield sendDataToSign(ledger, neon_core_1.u.num2hexstring(network, 4, true), 1);
        const chunks = payload.match(/.{1,510}/g) || [];
        try {
            for (let i = 0; i < chunks.length - 1; i++) {
                yield sendDataToSign(ledger, chunks[i], 2 + i);
            }
            const response = yield sendDataToSign(ledger, chunks[chunks.length - 1], 2 + chunks.length, true);
            // Expected signature + 2 status bytes to be returned here upon completion
            if (response.length <= 2) {
                throw new Error(`No more data but Ledger did not return signature!`);
            }
            return DerToHexSignature(response.toString("hex"));
        }
        catch (e) {
            if ((0, ErrorCode_1.looksLikeTransportStatusError)(e)) {
                throw (0, ErrorCode_1.evalTransportError)(e);
            }
            throw e;
        }
    });
}
exports.getSignature = getSignature;
/**
 * Converts a signature in DER format to HEX format.
 * @param response - signature in DER format
 * @returns Signature in HEX format (64 bytes)
 */
function DerToHexSignature(response) {
    const ss = new neon_core_1.u.StringStream(response);
    // The first byte is format. It is usually 0x30 (SEQ) or 0x31 (SET)
    // The second byte represents the total length of the DER module.
    ss.read(2);
    // Now we read each field off
    // Each field is encoded with a type byte, length byte followed by the data itself
    ss.read(1); // Read and drop the type
    const r = ss.readVarBytes();
    ss.read(1);
    const s = ss.readVarBytes();
    // We will need to ensure both integers are 32 bytes long
    const integers = [r, s].map((i) => {
        if (i.length < 64) {
            i = "0".repeat(i.length - 64) + i;
        }
        if (i.length > 64) {
            i = i.substr(-64);
        }
        return i;
    });
    return integers.join("");
}
exports.DerToHexSignature = DerToHexSignature;
